import SixDigitCodeInput from "@via/components/code-input/six-digit-code-input.tsx";
import {
  mfaErrorMessage,
  useMfaVerify,
} from "@via/pages/authentication/mfa/mfa.api.tsx";
import {
  ChallengeGone,
  LockedNotice,
} from "@via/pages/authentication/mfa/mfa-notices.tsx";
import type {
  MfaChallengeStatusResponse,
  MfaSessionResponse,
  MfaVerifyRequestBody,
} from "common";
import { type JSX, useState } from "react";

/** The sign-in code: six digits from the authenticator app, or one recovery code. */
export default function MfaCodeScreen({
  status,
  onSession,
}: {
  status: MfaChallengeStatusResponse;
  onSession: (response: MfaSessionResponse) => void;
}): JSX.Element {
  const verify = useMfaVerify();
  const [remember, setRemember] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [attempt, setAttempt] = useState(0);
  const error = verify.isError ? mfaErrorMessage(verify.error) : null;
  const lockedUntil = error?.code === "locked" ? error.lockedUntil : undefined;
  const blocked = verify.isPending || lockedUntil !== undefined;
  const submit = (body: MfaVerifyRequestBody) =>
    verify.mutate(body, {
      onSuccess: onSession,
      onError: () => setAttempt(a => a + 1),
    });

  if (error?.code === "challenge_invalid") return <ChallengeGone />;

  return (
    <div>
      <h2 className="via-h2 text-center text-2xl font-bold">Enter your code</h2>
      <p className="mt-2 text-center text-sm text-gray-600">
        {useRecovery
          ? "Enter one of the recovery codes you saved when you set up multi-factor sign-in. Each code works once."
          : "Open your authenticator app (Duo Mobile, Google Authenticator, Microsoft Authenticator or another) and enter the 6 digit code for Foundry Connect."}
      </p>
      {!useRecovery ? (
        <div className="mt-6">
          <SixDigitCodeInput
            resetKey={attempt}
            disabled={blocked}
            onComplete={code => submit({ code, rememberDevice: remember })}
          />
          {status.rememberDeviceOffered && (
            <label className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
              />
              Remember this browser for {status.reauthDays} days
            </label>
          )}
        </div>
      ) : (
        <form
          className="mt-6 space-y-3"
          onSubmit={e => {
            e.preventDefault();
            submit({ recoveryCode: recoveryCode.trim() });
          }}>
          <label
            htmlFor="mfa-recovery-code"
            className="block text-sm font-medium leading-6 text-gray-900">
            Recovery code
          </label>
          <input
            id="mfa-recovery-code"
            autoComplete="off"
            value={recoveryCode}
            onChange={e => setRecoveryCode(e.target.value)}
            placeholder="xxxxx-xxxxx"
            className="block w-full rounded-md border-0 py-1.5 font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          />
          <button
            type="submit"
            disabled={blocked || recoveryCode.trim().length < 10}
            className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
            Sign in
          </button>
        </form>
      )}
      {lockedUntil ? (
        <div className="mt-6">
          <LockedNotice until={lockedUntil} />
        </div>
      ) : (
        error && (
          <p
            role="alert"
            className="mt-4 text-center text-sm font-semibold text-rose-600">
            {error.message}
          </p>
        )
      )}
      <button
        type="button"
        className="mt-6 w-full text-sm font-semibold text-indigo-600 hover:text-indigo-500"
        onClick={() => {
          verify.reset();
          setUseRecovery(v => !v);
        }}>
        {useRecovery ? "Use my authenticator app" : "Use a recovery code"}
      </button>
    </div>
  );
}
